import { useMemo } from 'react';

import { MedicalSpecialty, SpecialtyLevel } from '@dreryk/egabinet-personnel-frontend-api';

import { Bean, FormContextValues } from '@dreryk/react-common';
import {
   createDynamicColumnsWidth,
   InteractiveTable,
   InteractiveTableColumnConfiguration,
} from '@dreryk/react-components';

import { personnelTranslationKeys } from '../../../../../translations';

import { MedicalSpecialtyAutosuggestCell } from './MedicalSpecialtyAutosuggestCell';
import { SpecialtyLevelSelectCell } from './SpecialtyLevelSelectCell';

export type DoctorSpecialty = {
   medicalSpecialty: MedicalSpecialty | null;
   specialtyLevel: SpecialtyLevel;
};

export type DoctorSpecialtiesValue = { doctorSpecialties: DoctorSpecialty[] | undefined };

export type SpecialtiesBean = {
   specialties: DoctorSpecialty[] | null;
   noSpecialty: boolean;
};

const useColumns = (): InteractiveTableColumnConfiguration<DoctorSpecialty>[] => {
   return useMemo<InteractiveTableColumnConfiguration<DoctorSpecialty>[]>(() => {
      const columnWidths = createDynamicColumnsWidth({
         medicalSpecialty: '425px',
         specialtyLevel: '172px',
         options: '24px',
      });

      return [
         {
            name: 'medicalSpecialty',
            label: personnelTranslationKeys.specialtiesTable.medicalSpecialty,
            width: columnWidths.medicalSpecialty,

            renderer: ({ bean, index }) => (
               <MedicalSpecialtyAutosuggestCell index={index} defaultValue={bean.medicalSpecialty ?? null} />
            ),
         },
         {
            name: 'specialtyLevel',
            label: personnelTranslationKeys.specialtiesTable.specialtyLevel,
            width: columnWidths.specialtyLevel,

            renderer: ({ bean, index }) => (
               <SpecialtyLevelSelectCell index={index} defaultValue={bean.specialtyLevel} />
            ),
         },
         {
            name: 'options',
            width: columnWidths.options,

            renderer: ({ index }) => <InteractiveTable.DeleteRowCell index={index} />,
         },
      ];
   }, []);
};

const createEmptyDoctorSpecialtyTableBean = (): DoctorSpecialty => ({
   medicalSpecialty: null,
   specialtyLevel: 'SPECIALIST',
});

type DoctorSpecialtiesInteractiveTableProps = Pick<FormContextValues<DoctorSpecialtiesValue>, 'register'>;

export const DoctorSpecialtiesInteractiveTable = (props: DoctorSpecialtiesInteractiveTableProps): JSX.Element => {
   const columns = useColumns();
   return (
      <InteractiveTable<DoctorSpecialty, Bean>
         testId={'doctorSpecialties'}
         name={'doctorSpecialties'}
         register={props.register}
         columns={columns}
      >
         <InteractiveTable.AddRowButton<DoctorSpecialty>
            label={personnelTranslationKeys.specialtiesTable.addSpecialty}
            onAddRow={createEmptyDoctorSpecialtyTableBean}
            behavior={'append'}
         />
      </InteractiveTable>
   );
};
